<template>
  <popup
    v-model="showPopup"
    @close="showPopup = false"
    :loading="loader.on"
    :title="t('title')"
    width="516px"
    class="dependency-warning"
  >
    <div class="dependency-warning__content">
      <p class="dependency-warning__label">
        {{ t("description") }}
      </p>

      <p class="dependency-warning__label dependency-warning__label--bold">{{ missingDependencies }}</p>

      <p class="dependency-warning__label">
        {{ t("instruction") }}
      </p>
    </div>

    <template #footer>
      <div class="dependency-warning__footer">
        <v-btn @click="showPopup = false" :disabled="loader.on" text large>{{ t("buttons.addLater") }}</v-btn>

        <v-btn @click="addMissingInstances" :disabled="loader.on" large color="primary">
          {{ t("buttons.addInstances") }}
        </v-btn>
      </div>
    </template>
  </popup>
</template>

<script lang="ts">
import { defineComponent, computed } from "@vue/composition-api"
import { useStore } from "@/composable/useStore"
import { createTranslationModule } from "@/plugins/i18n"
import Loader from "@/utils/loader"
import Popup from "@/components/popup/PopupRoot.vue"
import Utils from "@/utils/utils"
import router from "@/router"
import "../../scss/_dependency-warning.scss"

export default defineComponent({
  name: "DependencyWarning",
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  setup(props, ctx) {
    const { spaceModule } = useStore()
    const t = createTranslationModule("WorkspaceEdit.dependencyWarning")
    const showPopup = Utils.vModel(props, ctx.emit)
    const loader = Loader()

    const hasMissingDependencies = computed(() => {
      const space = spaceModule.watching

      if (space && space.schema) {
        return space.schema.missingDependencies
      }

      return false
    })

    const missingDependencies = computed(() => {
      const space = spaceModule.watching
      const schema = space.schema
      const schemaDeployed = space.schemaDeployed
      const assetModels = spaceModule.assetModels

      if (space && space.schema?.services) {
        const services = space.schema.services

        if (services.length > 0) {
          const dependencies = services.map(s => s.dependsOn).flat()

          const uniqueDependencies = dependencies.reduce((collection: string[], s) => {
            if (collection.indexOf(s) === -1) {
              collection.push(s)
            }
            return collection
          }, [])

          const missingDependencyCollection = uniqueDependencies.filter(d => {
            const schemaDependency = schema.assets.some(dependency => dependency.type === d)

            if (!schemaDeployed) {
              return !schemaDependency
            } else {
              const schemaDeployedDependency = schemaDeployed.assets.some(dependency => dependency.type === d)
              return !schemaDeployedDependency
            }
          })

          return missingDependencyCollection
            .map(dependency => {
              const assetModel = assetModels.find(am => am.key === dependency)

              if (assetModel) {
                return assetModel.text
              } else {
                return dependency
              }
            })
            .join(", ")
        }
      }
    })

    const addMissingInstances = () => {
      loader.run(async () => {
        const spaceId = spaceModule.watching.id
        await spaceModule.addMissingDependencies(spaceId)
        await spaceModule.getWorkspace(spaceId)

        showPopup.value = false
      })
    }

    const shouldShow = () => {
      loader.run(async () => {
        const spaceId = spaceModule.watching.id

        if (!spaceId) {
          const routeSpaceId = router.currentRoute.params.id as string
          await spaceModule.getWorkspace(routeSpaceId)
        }

        if (hasMissingDependencies.value) {
          showPopup.value = true
        }
      })
    }
    shouldShow()

    return {
      t,
      missingDependencies,
      showPopup,
      addMissingInstances,
      loader
    }
  },
  components: {
    Popup
  }
})
</script>
